import React from 'react';
import PropTypes from 'prop-types';
import { TEXT_SIZE, TEXT_COLOR } from '@components/Text';
import Input from '@components/Input';
import RadioButtonsGroup from '@components/RadioButtonsGroup';
import { getFormattedTextCurry } from '@shared/Resources/Monitor/shared/utils/formattedText';
import { STIMULUS_CUSTOM_TYPE } from '@shared/Resources/Monitor/types';
import CustomTextsList from '../CustomTextsList';
import { CustomTypeSection } from './styles';

const getFormattedText = getFormattedTextCurry();

// Falls back to a single empty row so there is always something to edit/add to.
const getCustomTexts = (config) => (
  config.customTexts && config.customTexts.length ? config.customTexts : ['']
);

// Lets the therapist choose between a single custom text (legacy behaviour, kept as-is) or
// multiple custom texts that cycle across productions/repetitions during practice. Owns all the
// single-vs-multiple UI/UX so the parent CustomConfig only has to render this one component.
const CustomTextConfig = ({ config, updateField, updateConfig }) => {
  const customType = config.customType || STIMULUS_CUSTOM_TYPE.SINGLE;
  const customTexts = getCustomTexts(config);

  // Uses updateConfig (rather than updateField('customType')) so customType and customTexts are
  // always set together - otherwise selecting "Multiple" without editing anything would leave
  // customTexts unset and the patient view would silently fall back to the legacy customText.
  const changeCustomType = (nextType) => {
    const nextConfig = { ...config, customType: nextType };
    if (nextType === STIMULUS_CUSTOM_TYPE.MULTIPLE && !(config.customTexts && config.customTexts.length)) {
      nextConfig.customTexts = [config.customText || ''];
    }
    updateConfig(nextConfig);
  };

  const updateStringAt = (index) => ({ target: { value } }) => {
    const nextTexts = [...customTexts];
    nextTexts[index] = value;
    updateField('customTexts')(nextTexts);
  };

  const addString = () => {
    updateField('customTexts')([...customTexts, '']);
  };

  const removeStringAt = (index) => () => {
    updateField('customTexts')(customTexts.filter((_, i) => i !== index));
  };

  return (
    <React.Fragment>
      <CustomTypeSection className="customTypeSection">
        <RadioButtonsGroup
          className="customTypeOptions"
          value={customType}
          onChange={changeCustomType}
          options={[
            {
              value: STIMULUS_CUSTOM_TYPE.SINGLE,
              label: getFormattedText('Stimuli.customSingle', { textColor: TEXT_COLOR.DARK_BLUE, size: TEXT_SIZE.H4 }),
            },
            {
              value: STIMULUS_CUSTOM_TYPE.MULTIPLE,
              label: getFormattedText('Stimuli.customMultiple', { textColor: TEXT_COLOR.DARK_BLUE, size: TEXT_SIZE.H4 }),
            },
          ]}
        />
      </CustomTypeSection>
      {customType === STIMULUS_CUSTOM_TYPE.MULTIPLE ? (
        <CustomTextsList
          texts={customTexts}
          onChangeAt={updateStringAt}
          onAdd={addString}
          onRemoveAt={removeStringAt}
        />
      ) : (
        <Input
          className="stimulusTextInput"
          value={config.customText}
          onChange={({ target: { value } }) => updateField('customText')(value)}
          width={390}
          height={44}
        />
      )}
    </React.Fragment>
  );
};

CustomTextConfig.propTypes = {
  config: PropTypes.object,
  updateField: PropTypes.func,
  updateConfig: PropTypes.func,
};

export default CustomTextConfig;
